import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditBlogModal from 'components/admin/common/modal/Blog/EditBlogModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';

const Blog = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createBlogData,
        postError: creatingError,
        postLoading: creating,
        putData: updateBlogData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteBlogData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.BLOGS);

    const { postData: createMediaItems, loading: uploadingImages } = usePost(API_ENDPOINTS.MEDIA_ITEMS);
    const { deleteData: deleteMediaItem } = useDelete(API_ENDPOINTS.MEDIA_ITEMS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteBlog = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditBlog = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteBlog, modalData: blogToDelete, openModal: openDeleteBlog, closeModal: closedDeleteBlog } = useModalState({
        onClose: handleCloseDeleteBlog
    });

    const { isOpen: isOpenEditBlog, modalData: selectedBlog, openModal: openEditBlog, closeModal: closedEditBlog } = useModalState({
        onClose: handleCloseEditBlog
    });

    const validationRules = [
        { field: 'section_subtitle', method: 'required' },
        { field: 'section_title', method: 'required' },
        { field: 'slug', method: ['required', 'validUrlCharacters'] },
        { field: 'content', method: 'required' }
    ];

    const handledeleteMediaItem = async (id) => {
        try {
            await deleteMediaItem(id);
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleModalSave = useCallback(async (updatedBlog, galleryImages) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedBlog, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setErrors({});
        const forms = galleryImages
            ?.filter((image) => {
                const fileType = image?.file?.type || image?.file?.path.split('.').pop();
                return image.file && image.file.path && !image.file.path.startsWith('storage/') && (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif'].includes(fileType));
            })
            ?.map((image) => ({
                blog_id: updatedBlog.id || null,
                project_id: '',
                type: 'image',
                src: image.file,
            }));

        try {
            if (updatedBlog.id) {
                const { media_item, slide_sections, ...blogData } = updatedBlog;
                await updateBlogData(updatedBlog.id, blogData, true);
                if (forms?.length > 0) {
                    for (const form of forms) {
                        form.blog_id = updatedBlog.id;
                        await createMediaItems(form, true);
                    }
                }
                setSuccessMessage("Blog actualizado con éxito.");
            } else {
                const createdBlog = await createBlogData(updatedBlog, true);
                if (forms?.length > 0) {
                    for (const form of forms) {
                        form.blog_id = createdBlog.id;
                        await createMediaItems(form, true);
                    }
                }
                setSuccessMessage("Blog creado con éxito.");
            }
            closedEditBlog();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateBlogData, createBlogData, createMediaItems, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newBlog = {
            section_subtitle: '',
            section_title: '',
            content: '',
            src: '',
            slug: ''
        };
        openEditBlog(newBlog);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (blogToDelete) {
            try {
                await deleteBlogData(blogToDelete.id);
                setSuccessMessage("Blog eliminado con éxito.");
                refetch();
                closedDeleteBlog();
            } catch (error) {
                console.error(error);
            }
        }
    }, [blogToDelete, deleteBlogData, refetch]);

    const columns = useMemo(() => [
        { field: 'section_title', header: 'Título de la Sección', sortable: true },
        { field: 'section_subtitle', header: 'Subtítulo de la Sección', sortable: true },
        { field: 'slug', header: 'Slug', sortable: true }
    ], []);

    const memoizedGalleryImages = useMemo(() => selectedBlog?.media_item || [], [selectedBlog]);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Blog</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Blog
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditBlog}
                onDelete={openDeleteBlog}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="section_title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditBlogModal
                show={isOpenEditBlog}
                blog={selectedBlog}
                handleClose={closedEditBlog}
                handleSave={handleModalSave}
                updating={updating || creating || uploadingImages}
                errors={errors}
                requestError={creatingError || updatingError}
                deleteMediaItem={handledeleteMediaItem}
                initialImages={memoizedGalleryImages}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteBlog}
                onHide={closedDeleteBlog}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Blog);